// src/pages/touchPoints/timeline/layout.js
// Grid/stack layout: rows = days, columns = journeys. Each day is one horizontal
// band that hugs its content: every touchpoint on that day sits at the SAME y
// (parallel) in its own column; a sub-journey triggered the same day is pushed one
// slot below its trigger so its connector flows down. Every buffer is a single
// `pad`: pad above a header, pad between header and its card, pad below the last
// card. Header height is per-journey (varies with segment chips) so 2-row headers
// never overlap their card.
import { isoToDays } from '../../utility/isoDuration';

const DEFAULTS = {
  cardH: 170,    // rendered touchpoint card height (canvas passes the real value)
  headerH: 80,   // fallback header height when no per-journey headerHeightOf is given
  colW: 240, railLeft: 110, topPad: 24,
  pad: 16,       // universal buffer above/below cards and headers
  gap: 24,       // gap between two messages stacked in the same column on the same day
  headerHeightOf: null, // optional (journeyId) => px; falls back to headerH
};

export function layout(model, options = {}) {
  const opts = { ...DEFAULTS, ...options };
  const headerH = (jid) => (opts.headerHeightOf ? opts.headerHeightOf(jid) : opts.headerH);
  const cardHeightOf = (tp) => (opts.cardHeightOf ? opts.cardHeightOf(tp) : opts.cardH);

  // Pack journeys into shared columns ("lanes"): two journeys can share a column
  // when their day-ranges don't overlap, so a deep tree of sub-journeys doesn't
  // sprawl sideways. Greedy first-fit by start day keeps the root (earliest) at lane 0.
  const range = {};
  model.journeys.forEach((journey) => {
    const tpDays = journey.touchpoints.map((touchpoint) => touchpoint.day);
    range[journey.journeyId] = [Math.min(...tpDays), Math.max(...tpDays)];
  });
  const preIndex = {}; model.journeys.forEach((journey, i) => { preIndex[journey.journeyId] = i; });
  const ordered = [...model.journeys].sort((a, b) => (
    range[a.journeyId][0] - range[b.journeyId][0] || preIndex[a.journeyId] - preIndex[b.journeyId]
  ));
  const laneEnd = []; // last day occupied in each lane
  const colIndex = {};
  ordered.forEach((journey) => {
    const [startDay, endDay] = range[journey.journeyId];
    let lane = laneEnd.findIndex((end) => end < startDay);
    if (lane === -1) { lane = laneEnd.length; laneEnd.push(endDay); } else { laneEnd[lane] = endDay; }
    colIndex[journey.journeyId] = lane;
  });
  const laneCount = laneEnd.length;
  const colX = (jid) => opts.railLeft + 90 + colIndex[jid] * opts.colW;
  const columns = model.journeys.map((journey) => ({
    journeyId: journey.journeyId, name: journey.name, x: colX(journey.journeyId),
    primaryTriggerTpId: journey.primaryTriggerTpId, isRoot: journey.isRoot,
  }));

  const raw = [];
  model.journeys.forEach((journey) => journey.touchpoints.forEach((tp) => raw.push({
    tp, journeyId: journey.journeyId, day: tp.day, isEntry: tp.isEntry, col: colIndex[journey.journeyId],
  })));

  // which touchpoint triggers each entry, and what day every touchpoint lands on —
  // used to push a same-day-triggered sub-journey BELOW its trigger so the connector flows down
  const dayOfTp = {}; raw.forEach((rec) => { dayOfTp[rec.tp.touchpoint_id] = rec.day; });
  const entryTrigger = {};
  model.journeys.forEach((journey) => {
    const entry = journey.touchpoints.find((touchpoint) => touchpoint.isEntry);
    if (entry && journey.primaryTriggerTpId != null) entryTrigger[entry.touchpoint_id] = journey.primaryTriggerTpId;
  });

  // days on which a journey's last touchpoint falls — its panel ends there, so that
  // band needs an extra pad below the panel to keep it off the next day line
  const endsOnDay = new Set(model.journeys.map((journey) => Math.max(...journey.touchpoints.map((touchpoint) => touchpoint.day))));

  const byDay = {}; raw.forEach((rec) => { (byDay[rec.day] = byDay[rec.day] || []).push(rec); });
  const days = Object.keys(byDay).map(Number).sort((a, b) => a - b);

  const cards = [];
  const bands = [];
  let rowTop = opts.topPad;
  days.forEach((day) => {
    const dcards = byDay[day];
    // base slot: stack same-column messages by ordinal
    const slot = {};
    const byCol = {}; dcards.forEach((dcard) => { (byCol[dcard.col] = byCol[dcard.col] || []).push(dcard); });
    Object.keys(byCol).forEach((col) => {
      byCol[col].sort((a, b) => a.tp.ordinal - b.tp.ordinal);
      byCol[col].forEach((dcard, idx) => { slot[dcard.tp.touchpoint_id] = idx; });
    });
    // push-down: an entry whose trigger is on this same day sits one slot below it
    // (dcards are in journey pre-order, so a trigger is always assigned before its child).
    // Shift the entry AND its same-journey, same-day continuations by the same delta so
    // their ordinal stacking is preserved — otherwise an appended same-day touchpoint keeps
    // its lower ordinal-slot and collides with the pushed-down entry (drawn on top of it).
    dcards.forEach((dcard) => {
      const triggerId = entryTrigger[dcard.tp.touchpoint_id];
      if (triggerId != null && dayOfTp[triggerId] === day) {
        const delta = ((slot[triggerId] ?? 0) + 1) - slot[dcard.tp.touchpoint_id];
        if (delta > 0) {
          dcards.forEach((sibling) => {
            if (sibling.journeyId === dcard.journeyId && sibling.tp.ordinal >= dcard.tp.ordinal) slot[sibling.tp.touchpoint_id] += delta;
          });
        }
      }
    });
    // place each slot row. An entry card sits tight under its OWN header
    // (pad → header → pad), so its box is snug regardless of neighbours. A
    // continuation card sits at the row baseline — the tallest entry's card level —
    // so a same-day continuation stays parallel with the entry beside it. Entries
    // with shorter headers therefore sit a little higher (shorter box), with no slack.
    const bySlot = {}; dcards.forEach((dcard) => { const slotIdx = slot[dcard.tp.touchpoint_id]; (bySlot[slotIdx] = bySlot[slotIdx] || []).push(dcard); });
    const maxSlot = dcards.reduce((max, dcard) => Math.max(max, slot[dcard.tp.touchpoint_id]), 0);
    const cardTop = {};
    let yCursor = rowTop;
    for (let slotIdx = 0; slotIdx <= maxSlot; slotIdx += 1) {
      const inSlot = bySlot[slotIdx] || [];
      const entries = inSlot.filter((dcard) => dcard.isEntry);
      const maxH = entries.length ? Math.max(...entries.map((entry) => headerH(entry.journeyId))) : 0;
      const baseline = entries.length ? opts.pad + maxH + opts.pad : (slotIdx === 0 ? opts.pad : opts.gap);
      inSlot.forEach((dcard) => {
        const offset = dcard.isEntry ? opts.pad + headerH(dcard.journeyId) + opts.pad : baseline;
        cardTop[dcard.tp.touchpoint_id] = yCursor + offset;
      });
      const slotH = inSlot.length ? Math.max(...inSlot.map((dcard) => cardHeightOf(dcard.tp))) : opts.cardH;
      yCursor += baseline + slotH;
    }
    dcards.forEach((dcard) => {
      cards.push({
        touchpoint: dcard.tp, journeyId: dcard.journeyId, x: colX(dcard.journeyId),
        y: cardTop[dcard.tp.touchpoint_id],
        day, isEntry: dcard.isEntry, waitDays: isoToDays(dcard.tp.wait_time),
      });
    });
    // pad below the cards; an extra pad when a panel ends here so it clears the day line
    const rowHeight = (yCursor + opts.pad + (endsOnDay.has(day) ? opts.pad : 0)) - rowTop;
    bands.push({ day, top: rowTop, height: rowHeight, count: dcards.filter((dcard) => !dcard.tp.placeholder).length, label: `Day ${day + 1}` });
    rowTop += rowHeight;
  });

  return {
    cards, columns, bands,
    width: opts.railLeft + 90 + laneCount * opts.colW,
    height: rowTop + 80,
  };
}
